import { ValidatorFn } from "@angular/forms";
import { ColumnDef } from "./column-definition";
import { SupplierInterface } from "./supplier-interface";
import { Validators } from '@angular/forms';

/** Validators for fields */
const validators = new Map<string, ValidatorFn[]>([
    ["merchantID", [
        Validators.required,
        Validators.maxLength(6),
        Validators.minLength(6),
        Validators.pattern(/^[0-9]+$/),
    ]],
    ["firstName", [
        Validators.required,
    ]],
    ["lastName", [
        Validators.required,
    ]],
    ["emailAddress", [
        Validators.required,
        Validators.email,
    ]],
    ["phoneNumber", [
        Validators.required,
        Validators.pattern(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/),
    ]],
    ["streetAddress", [
        Validators.required,
    ]],
    ["city", [
        Validators.required,
    ]],
    ["stateAbbr", [
        Validators.required,
        Validators.maxLength(2),
    ]],
    ["postalCode", [
        Validators.required,
        Validators.pattern(/^\d{5}(?:[-\s]\d{4})?$/),
    ]],
    ["status", [
        Validators.required,
    ]],
    ["macTruckSignupID", [
        Validators.required,
        Validators.pattern(/^[0-9]+$/),
    ]],
    ["merchantName", [
        Validators.required,
    ]],
    ["division", [
        Validators.required,
    ]],
    ["region", [
        Validators.required,
    ]],
    ["district", [
        Validators.required,
    ]],
    ["ownerMerchantID", [
        Validators.required,
        Validators.pattern(/^[0-9]+$/),
    ]],
    ["ownerEmail", [
        Validators.email,
    ]],
    ["ownerPhone", [
        Validators.pattern(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/),
    ]],
    ["statusDate", [
        Validators.required,
        Validators.pattern(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/),
    ]],
]);

export class Supplier {
    /** Attribute fields */
    private _id: number;
    private _firstName: string;
    private _lastName: string;
    private _emailAddress: string;
    private _phoneNumber: string;
    private _streetAddress: string;
    private _city: string;
    private _stateAbbr: string;
    private _postalCode: string;
    private _merchantID: string;
    private _status: 'Active' | 'Inactive';
    private _macTruckSignupID: number;
    private _merchantName: string;
    private _division: string;
    private _region: string;
    private _district: string;
    private _ownerMerchantID: string;
    private _ownerEmail: string;
    private _ownerPhone: string;
    private _statusDate: string;

    /** Validators for supplier forms */
    public static validators = validators;

    /**
     * Construct the supplier from an interface
     */
    constructor(intf: SupplierInterface) {
        this._id = intf.id;
        this._firstName = intf.firstName?.trim() || "";
        this._lastName = intf.lastName?.trim() || "";
        this._emailAddress = intf.emailAddress?.trim() || "";
        this._phoneNumber = intf.phoneNumber?.trim() || "";
        this._streetAddress = intf.streetAddress?.trim() || "";
        this._city = intf.city?.trim() || "";
        this._stateAbbr = intf.stateAbbr?.trim() || "";
        this._postalCode = intf.postalCode?.trim() || "";
        this._merchantID = intf.merchantID?.trim() || "";
        this._status = intf.status;
        this._macTruckSignupID = intf.macTruckSignupID;
        this._merchantName = intf.merchantName?.trim() || "";
        this._division = intf.division?.trim() || "";
        this._region = intf.region?.trim() || "";
        this._district = intf.district?.trim() || "";
        this._ownerMerchantID = intf.ownerMerchantID?.trim() || "";
        this._ownerEmail = intf.ownerEmail?.trim() || "";
        this._ownerPhone = intf.ownerPhone?.trim() || "";
        this._statusDate = intf.statusDate?.trim() || "";
    }

    /**
     * Determine if two suppliers are equal
     * 
     * @param sup The other suppliers to check
     * @returns True if suppliers are equal
     */
    public equals(sup: Supplier): boolean {
        return this.id === sup.id &&
                this._firstName === sup.firstName &&
                this._lastName === sup.lastName &&
                this._emailAddress === sup.emailAddress &&
                this._phoneNumber === sup.phoneNumber &&
                this._streetAddress === sup.streetAddress &&
                this._city === sup.city &&
                this._stateAbbr === sup.stateAbbr &&
                this._postalCode === sup.postalCode &&
                this._merchantID === sup.merchantID &&
                this._status === sup.status &&
                this._macTruckSignupID === sup.macTruckSignupID &&
                this._merchantName === sup.merchantName &&
                this._division === sup.division &&
                this._region === sup.region &&
                this._district === sup.district &&
                this._ownerMerchantID === sup.ownerMerchantID  &&
                this._ownerEmail === sup.ownerEmail &&
                this._ownerPhone === sup.ownerPhone &&
                this._statusDate === sup.statusDate;
    }

    /**
     * Return a copy of this supplier
     */
    public copyOf(): Supplier {
        return new Supplier(this.asInterface());
    }

    /**
     * Return the supplier as an interface
     */
    public asInterface(): SupplierInterface {
        const intf: SupplierInterface = {
            id: this.id,
            firstName: this._firstName,
            lastName: this._lastName,
            emailAddress: this._emailAddress,
            phoneNumber: this._phoneNumber,
            streetAddress: this._streetAddress,
            city: this._city,
            stateAbbr: this._stateAbbr,
            postalCode: this._postalCode,
            merchantID: this._merchantID,
            status: this._status,
            macTruckSignupID: this._macTruckSignupID,
            merchantName: this._merchantName,
            division: this._division,
            region: this._region,
            district: this._district,
            ownerMerchantID: this._ownerMerchantID,
            ownerEmail: this._ownerEmail,
            ownerPhone: this._ownerPhone,
            statusDate: this._statusDate,
        };

        return intf;
    }

    /**
     * Return the supplier as a string representation, used for filtering/searching
     * 
     * We use a unicode full block as a delimeter, so the only string matches that
     * return are from our fields (unless the user is typing the unicode full block
     * character like a maniac)
     */
    public toString(defs: ColumnDef[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /** Setters */
    set id(id: number) {
        this._id = id;
    }

    set firstName(firstName: string) {
        this._firstName = firstName;
    }

    set lastName(lastName: string) {
        this._lastName = lastName;
    }

    set emailAddress(emailAddress: string) {
        this._emailAddress = emailAddress;
    }

    set phoneNumber(phoneNumber: string) {
        this._phoneNumber = phoneNumber;
    }

    set streetAddress(streetAddress: string) {
        this._streetAddress = streetAddress;
    }

    set city(city: string) {
        this._city = city;
    }

    set stateAbbr(stateAbbr: string) {
        this._stateAbbr = stateAbbr;
    }

    set postalCode(postalCode: string) {
        this._postalCode = postalCode;
    }

    set merchantID(merchantID: string) {
        this._merchantID = merchantID;
    }

    set status(status: 'Active' | 'Inactive') {
        this._status = status;
    }

    set macTruckSignupID(macTruckSignupID: number) {
        this._macTruckSignupID = macTruckSignupID
    }

    set merchantName(merchantName: string) {
        this._merchantName = merchantName;
    }

    set division(division: string) {
        this._division = division;
    }

    set region(region: string) {
        this._region = region;
    }

    set district(district: string) {
        this._district = district;
    }

    set ownerMerchantID(ownerMerchantID: string) {
        this._ownerMerchantID = ownerMerchantID;
    }

    set ownerEmail(ownerEmail: string) {
        this._ownerEmail = ownerEmail;
    }

    set ownerPhone(ownerPhone: string) {
        this._ownerPhone = ownerPhone;
    }

    set statusDate(statusDate: string) {
        this._statusDate = statusDate;
    }

    /** Getters */
    get id(): number {
        return this._id;
    }

    get firstName(): string {
        return this._firstName;
    }

    get lastName(): string {
        return this._lastName;
    }

    get emailAddress(): string {
        return this._emailAddress;
    }

    get phoneNumber(): string {
        return this._phoneNumber;
    }

    get streetAddress(): string {
        return this._streetAddress;
    }

    get city(): string {
        return this._city;
    }

    get stateAbbr(): string {
        return this._stateAbbr;
    }

    get postalCode(): string {
        return this._postalCode;
    }

    get merchantID(): string {
        return this._merchantID;
    }

    get status(): 'Active' | 'Inactive' {
        return this._status;
    }

    get macTruckSignupID(): number {
        return this._macTruckSignupID;
    }

    get merchantName(): string {
        return this._merchantName;
    }

    get division(): string {
        return this._division;
    }

    get region(): string {
        return this._region;
    }

    get district(): string {
        return this._district;
    }

    get ownerMerchantID(): string {
        return this._ownerMerchantID;
    }

    get ownerEmail(): string {
        return this._ownerEmail;
    }

    get ownerPhone(): string {
        return this._ownerPhone;
    }

    get statusDate(): string {
        return this._statusDate;
    }

    /** Specialized getters */
    get name(): string {
        return `${this._firstName} ${this._lastName}`;
    }

    get address(): string {
        return `${this.streetAddress}. ${this.city}, ${this.stateAbbr}. ${this.postalCode}`;
    }

    get calculatedStatus(): string {
        return this._macTruckSignupID === 0 ? "Inactive" : this._status;
    }

}

/**
 * Return an empty supplier
 */
export const emptySupplier = (): Supplier => {
    return new Supplier({
        id: 0,
        firstName: "",
        lastName: "",
        emailAddress: "",
        phoneNumber: "",
        streetAddress: "",
        city: "",
        stateAbbr: "",
        postalCode: "",
        merchantID: "",
        status: "Active",
        macTruckSignupID: 0,
        merchantName: "",
        division: "",
        region: "",
        district: "",
        ownerMerchantID: "0",
        ownerEmail: "",
        ownerPhone: "",
        statusDate: "",
    });
}